import { createApi } from "@reduxjs/toolkit/query/react";
import RtkBaseQueryWithReauth from "./RtkBaseQueryWithReauth";
import ApiPath from "./ConstantApi";
import {
  CartRequest,
  GetCartModel,
  BuyNowRequest,
} from "../Helpers/Interfaces/CartType";
import {
  MyOrderResponseModel,
  OrderData,
} from "../Helpers/Interfaces/OrderType";
import Config from "../Config";

export interface OrderResponse {
  _id: string;
  orderNumber: string;
  customer: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
  };
  totalAmount: number;
  status: "pending" | "processing" | "completed" | "cancelled";
  paymentStatus: "paid" | "unpaid" | "refunded";
  createdAt: string;
  errors: { [key: string]: string };
}

export interface OrderListResponse {
  data: OrderResponse[];
  totalRecords: number;
  totalPages: number;
  currentPage: number;
}

export interface OrderListParams {
  page: number;
  pageSize: number;
}

export interface OrderItem {
  _id: string;
  name: string;
  price: number;
  quantity: number;
  image: string;
}

export interface OrderDetailsResponse extends OrderResponse {
  items: OrderItem[];
  subtotal: number;
  shippingCost: number;
  paymentMethod: string;
  transactionId?: string;
  shippingAddress: string;
}

export interface OrderDetailsResponse2 extends OrderResponse {
  items: OrderItem[];
  subtotal: number;
  shippingCost: number;
  paymentMethod: string;
  transactionId?: string;
  shippingAddress: string;
}

export const OrderService = createApi({
  reducerPath: "Orders",
  baseQuery: RtkBaseQueryWithReauth(Config.BASE_URL),
  tagTypes: ["Orders", "Cart"],
  endpoints: (builder) => ({
    getOrderList: builder.query<OrderListResponse, OrderListParams>({
      query: (params) => ({
        url: `${ApiPath.getOrdersList}?page=${params.page}&pageSize=${params.pageSize}`,
        method: "GET",
      }),
      providesTags: ["Orders"],
    }),
    getOrderDetails: builder.query<OrderData, string | undefined>({
      query: (id) => ({
        url: `${ApiPath.getOrderDetails}/${id}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response.data.orderRes,
    }),

    /**@ADD_TO_CART */
    addToCart: builder.mutation<GetCartModel, CartRequest>({
      query: (product) => ({
        url: `${ApiPath.addToCart}`,
        method: "POST",
        body: product,
      }),
      invalidatesTags: ["Cart"],
    }),

    buyNow: builder.mutation<GetCartModel, BuyNowRequest>({
      query: (product) => ({
        url: `${ApiPath.buyNow}`,
        method: "POST",
        body: product,
      }),
      // invalidatesTags: ["Cart"],
    }),

    getBuyNow: builder.query<GetCartModel, { paymentMethod?: string }>({
      query: (params) => ({
        url: `${ApiPath.getBuyNow}${
          params?.paymentMethod ? `/${params.paymentMethod}` : "/Online"
        }`,
        method: "GET",
      }),
      // invalidatesTags: ["Cart"],
    }),

    removeBuyNowItem: builder.mutation<any, any>({
      query: (id) => ({
        url: `${ApiPath.removeBuyNowItem}`,
        method: "DELETE",
      }),
      // invalidatesTags: ["Cart"],
    }),

    getCart: builder.query<GetCartModel, { paymentMethod?: string } | void>({
      query: (params) => ({
        url: `${ApiPath.getCart}${
          params?.paymentMethod ? `/${params.paymentMethod}` : "/Online"
        }`,
        method: "GET",
      }),
      providesTags: ["Cart"],
    }),

    removeCartItem: builder.mutation<any, any>({
      query: (id) => ({
        url: `${ApiPath.removeCart}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Cart"],
    }),

    addAddress: builder.mutation<any, any>({
      query: (body) => ({
        url: `${ApiPath.createCustomerAddress}`,
        method: "POST",
        body: body,
      }),
    }),

    getAddresses: builder.query<any, void>({
      // query: () => ({
      //   url: `${ApiPath.getCustomerAddress}`,
      //   method: "GET",
      // }),
      query: () => {
        const raw = localStorage.getItem(
          `${process.env.REACT_APP_STORAGE_KEY}/accessToken`
        );
        const token = raw ? JSON.parse(raw) : null;
        return {
          url: `${ApiPath.getCustomerAddress}`,
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };
      },
    }),

    createOrderApi: builder.mutation<any, any>({
      query: (reqBody) => ({
        url: `${ApiPath.createOrder}`,
        method: "POST",
        body: reqBody,
      }),
    }),

    callStripePaymentApi: builder.query<
      any,
      { id: string; paymentMethod?: string }
    >({
      query: ({ id, paymentMethod }) => ({
        url: `${ApiPath.createStripePayment}/${id}${
          paymentMethod ? `?paymentType=${paymentMethod.toLowerCase()}` : ""
        }`,
        method: "GET",
      }),
    }),

    cancelOrderApi: builder.mutation<any, { orderNumber: string }>({
      query: ({ orderNumber }) => ({
        url: `${ApiPath.updateOrder}/${orderNumber}`,
        method: "PUT",
      }),
    }),

    downloadInvoiceApi: builder.query<Blob, { id: string; lang: "en" | "de" }>({
      query: ({ id, lang }) => ({
        url: `${ApiPath.downloadInvoice}/${id}/${lang}`,
        method: "GET",
        responseHandler: (response: any) => response.blob(), // ensure PDF comes as Blob
      }),
    }),

    // downloadInvoiceApi: builder.query<{ urls: string[] }, { id: string; lang: "en" | "de" }>({
    //   query: ({ id, lang }) => ({
    //     url: `${ApiPath.downloadInvoice}/${id}/${lang}`,
    //     method: "GET",
    //   }),
    // }),

    getCustomerOrderPin: builder.query<any, any>({
      query: (id) => ({
        url: `${ApiPath.getCustomerOrderPin}/${id}`,
        method: "GET",
      }),
    }),

    getMyAllOrders: builder.query<MyOrderResponseModel, string>({
      query: (searchQuery) => ({
        url: `${ApiPath.getMyOrders}${searchQuery}`,
        method: "GET",
      }),
    }),

    updateCustomerProfile: builder.mutation<any, { body: any; _id: string }>({
      query: (params) => ({
        url: `${ApiPath.updateCustomer}/${params?._id}`,
        method: "PUT",
        body: params.body,
      }),
    }),

    deleteCustomerAddressById: builder.mutation<
      any,
      { customerId: string; addressId: string }
    >({
      query: ({ customerId, addressId }) => ({
        url: `${ApiPath.deleteCustomerAddress}/${customerId}/${addressId}`,
        method: "DELETE",
      }),
    }),

    getorderitemswithpins: builder.query<any, string>({
      query: (id) => ({
        url: `${ApiPath.getorderitemswithpins}/${id}`,
        method: "GET",
      }),
    }),

    getOrderSocketStatus: builder.query<
      {
        status: "success" | "error" | "failed";
        processing: "STOP" | "DONE" | "CONTINUE";
      },
      string
    >({
      query: (orderNumber) => ({
        url: `${ApiPath.getOrderSocketStatus}/${orderNumber}`,
        method: "GET",
      }),
    }),

    downloadCreditMemo: builder.query<Blob, { creditMemoNumber: string; lang: "en" | "de" }>({
      query: ({ creditMemoNumber, lang }) => ({
        url: `${ApiPath.creditMemoDownload}/${creditMemoNumber}/${lang}`,
        method: "GET",
        responseHandler: (response: any) => response.blob(),
      }),
    }),

    getOrderCreditMemos: builder.query<any, string>({
      query: (orderNumber) => ({
        url: `${ApiPath.orderCreditMemos}/${orderNumber}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response.data,
    }),

    downloadRefundInvoice: builder.query<Blob, { refundInvoiceNumber: string; lang: "en" | "de" }>({
      query: ({ refundInvoiceNumber, lang }) => ({
        url: `${ApiPath.refundInvoiceDownload}/${refundInvoiceNumber}/${lang}`,
        method: "GET",
        responseHandler: (response: any) => response.blob(),
      }),
    }),

    getOrderRefundInvoices: builder.query<any, string>({
      query: (orderNumber) => ({
        url: `${ApiPath.orderRefundInvoices}/${orderNumber}`,
        method: "GET",
      }),
      transformResponse: (response: any) => response.data,
    }),
  }),
});

export const {
  useGetOrderListQuery,
  useLazyGetOrderListQuery,
  useGetOrderDetailsQuery,
  useLazyGetOrderDetailsQuery,
  useAddToCartMutation,
  useBuyNowMutation,
  useLazyGetBuyNowQuery,
  useRemoveBuyNowItemMutation,
  useLazyGetCartQuery,
  useGetCartQuery,
  useRemoveCartItemMutation,
  useAddAddressMutation,
  useGetAddressesQuery,
  useLazyDownloadInvoiceApiQuery,
  useLazyCallStripePaymentApiQuery,
  useCreateOrderApiMutation,
  useCancelOrderApiMutation,
  useLazyGetMyAllOrdersQuery,
  useLazyGetCustomerOrderPinQuery,
  useUpdateCustomerProfileMutation,
  useDeleteCustomerAddressByIdMutation,
  useLazyGetorderitemswithpinsQuery,
  useLazyGetOrderSocketStatusQuery,
  useLazyDownloadCreditMemoQuery,
  useGetOrderCreditMemosQuery,
  useLazyDownloadRefundInvoiceQuery,
  useGetOrderRefundInvoicesQuery,
} = OrderService;
